/**
 * Helpers d'échappement / construction des recherches texte PostgREST.
 *
 * Tout terme utilisateur interpolé dans une chaîne `.or('col.ilike.%term%,...')`
 * doit passer par `escapeOrSearchTerm`, et idéalement par `buildSearchOrConditions`
 * qui centralise la construction multi-champs (revue 2026-06-01).
 *
 * Deux classes de risque, vérifiées end-to-end via PostgREST :
 *  - Syntaxe `.or()` : `,` sépare les filtres et `(` `)` ouvrent des groupes.
 *    Non échappables dans un motif → un terme contenant `,(` provoque un
 *    HTTP 400 (PGRST100 « failed to parse logic tree »). On les RETIRE.
 *  - Wildcards de motif : côté PostgREST `*` ET `%` jouent le rôle de joker
 *    (`*` est traduit en `%`), `_` matche un caractère. Un terme contenant
 *    `*`/`%`/`_` élargit donc silencieusement la recherche. On les ÉCHAPPE en
 *    littéraux via `\` (ILIKE Postgres + parse PostgREST, vérifié : `4*Trophy`
 *    non échappé matchait les 3 produits ; `4\*Trophy` n'en matche aucun).
 *    `\` lui-même est échappé en premier pour rester littéral.
 */
export function escapeOrSearchTerm(term: string): string {
  return term
    .replace(/[\\%_*]/g, (c) => `\\${c}`) // wildcards (\\ % _ *) -> littéraux
    .replace(/[(),]/g, ''); // caractères de syntaxe .or() -> retirés
}

/**
 * Échappe une valeur pour la forme MÉTHODE supabase-js `.ilike(col, pattern)`
 * (≠ chaîne `.or(...)`). La lib paramètre la valeur, donc `, ( ) "` y sont déjà
 * LITTÉRAUX — rien à neutraliser de ce côté. Seuls `%` et `_` sont des wildcards
 * SQL LIKE ; `*` est un caractère LITTÉRAL en LIKE (contrairement à la forme
 * chaîne `.or()` où PostgREST traduit `*`→`%`). On échappe donc `\ % _` mais
 * PAS `*`. Garantit que le mode AND (méthode) et le mode OR (chaîne) traitent
 * `% _` à l'identique (revue 2026-06-01).
 */
export function escapeLikeMethod(value: string): string {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * Condition PostgREST TOUJOURS FAUSSE pour un champ donné (`field IS NULL ET
 * field IS NOT NULL`). Field-agnostique (tout type de colonne), vérifiée e2e :
 * 0 ligne. Sert de "match-nothing" quand un terme de recherche / filtre se
 * réduit à rien après échappement, pour ne pas tomber dans un `ilike.%%`
 * (match-all) ou un filtre purement supprimé (revue 2026-06-01).
 */
export function alwaysFalseCondition(field: string): string {
  return `and(${field}.is.null,${field}.not.is.null)`;
}

/**
 * Retour réunion 07/07 (E) : le n° BED a « le même format que le BPI avec BED au
 * milieu » (`BED-<année>-<n° équipage>` ↔ `BPI-<année>-<n° équipage>`). Plutôt
 * que de matérialiser une colonne `bed_code` dédiée, on recherche un n° BED en
 * substituant `BED`→`BPI` dans le terme et en le cherchant sur `bpi_code`
 * (crews_filter_view et, après migration 00151, contacts_filter_view). Le variant
 * n'est produit que si le terme ressemble à un code BED (« bed » suivi, après
 * séparateur optionnel, d'un chiffre) → un nom comme « Bedard » n'en génère pas.
 *
 * Ex : 'BED-2027-0042' -> ['BED-2027-0042', 'BPI-2027-0042']
 */
export function expandBpiBedTerms(rawTerm: string): string[] {
  const terms = [rawTerm];
  if (/bed[\s\-_]*\d/i.test(rawTerm)) {
    const bpiVariant = rawTerm.replace(/bed/i, 'BPI');
    if (bpiVariant !== rawTerm) terms.push(bpiVariant);
  }
  return terms;
}

/**
 * Forme d'un code BPI/BED une fois RECOLLÉ (séparateurs retirés) : préfixe
 * alphanumérique optionnel (`SD`, `9274L`), `BPI`/`BED`, puis des chiffres.
 * Un nom (`Bedard` : pas de chiffre), un email (`@`/`.` survivent au recollage)
 * ou un numéro nu (`0040` : pas de `BPI`/`BED`) n'y répond pas.
 */
const BPI_BED_CODE_SHAPE = /^[a-z0-9]*(?:bpi|bed)\d+$/i;

/**
 * Gabarit tireté observé en base : `{préfixe}BPI-{millésime}-{n°}`. Le millésime
 * est contraint à `20\d\d` — c'est ce qui empêche le format produit
 * `9274LBPI000001` (6 chiffres commençant par `0000`) d'engendrer une variante
 * re-tiretée parasite (`9274LBPI-0000-01`).
 */
const BPI_BED_HYPHEN_TEMPLATE = /^([a-z0-9]*(?:bpi|bed))(20\d{2})(\d{2,6})$/i;

/**
 * Retour Robin 26/07 : « la recherche par n° BPI ne prend pas les tirets en
 * compte ». DEUX familles de codes coexistent — constaté sur la dev, 109 lignes
 * `form_submissions.bpi_code` :
 *  - SANS séparateur, le format que produit la RPC `create_payment_case_from_submission`
 *    via `build_bpi_reference` (00107) : `9{YY}{event}BPI{NNNNNN}` → `9274LBPI000001` ;
 *  - AVEC tirets à gabarit fixe, le format des jeux d'essai (108 lignes) :
 *    `{préfixe}BPI-{millésime}-{NNNN}` → `SDBPI-2027-0040`.
 *
 * Un `ilike.%terme brut%` ne franchit jamais cette frontière : `9274-LBPI-000001`
 * ne trouve pas le code collé, `SDBPI20270040` ne trouve pas le code tireté. On
 * ajoute donc au terme brut deux variantes normalisées :
 *   (a) le terme RECOLLÉ — tirets, underscores et espaces retirés ;
 *   (b) le terme RE-TIRETÉ au gabarit ci-dessus, calculé depuis le recollé.
 *
 * Garde symétrique de celle de `expandBpiBedTerms` : rien n'est produit si le
 * terme ne RESSEMBLE pas à un code. Deuxième garde, pour les termes composites :
 * un code ne porte qu'UN bloc de lettres, donc « dupont BED-2027-0042 » (deux
 * mots porteurs de lettres) n'est jamais recollé en `dupontBED20270042` — seul
 * le mot code l'est, mot par mot (cf. buildSearchOrConditions).
 *
 * Ex : 'SDBPI20270040'   -> ['SDBPI20270040', 'SDBPI-2027-0040']
 *      'SDBPI-2027-0040' -> ['SDBPI-2027-0040', 'SDBPI20270040']
 *      '9274-LBPI-000001'-> ['9274-LBPI-000001', '9274LBPI000001']
 *      'Dupont'          -> ['Dupont']
 */
export function expandCodeSeparatorVariants(rawTerm: string): string[] {
  const glued = rawTerm.replace(/[\s\-_]+/g, '');
  if (!BPI_BED_CODE_SHAPE.test(glued)) return [rawTerm];

  const words = rawTerm.trim().split(/\s+/);
  if (words.filter((word) => /[a-z]/i.test(word)).length > 1) return [rawTerm];

  const variants = [rawTerm];
  const add = (variant: string) => {
    if (variant !== '' && !variants.includes(variant)) variants.push(variant);
  };
  add(glued);
  add(glued.replace(BPI_BED_HYPHEN_TEMPLATE, '$1-$2-$3'));
  return variants;
}

/**
 * Construit la chaîne de conditions `.or()` pour une recherche « contient »
 * (ILIKE %mot%) sur plusieurs champs. Le terme est découpé en mots (espaces) :
 * chaque mot doit matcher au moins un champ (AND de groupes OR), si bien que
 * « jean dupont » trouve first_name=Jean / last_name=Dupont. Un seul mot garde
 * la forme plate historique. Chaque mot est échappé UNE fois ; les noms de
 * champs ne le sont jamais (config développeur, pas une entrée utilisateur).
 * À passer directement à `query.or(...)`.
 *
 * Retour 07/07 (E) : un terme n° BED est étendu à son équivalent BPI (cf.
 * expandBpiBedTerms) → les conditions couvrent bpi_code pour tous les champs.
 *
 * Retour Robin 26/07 : un terme qui ressemble à un n° BPI/BED est en outre
 * cherché RECOLLÉ et RE-TIRETÉ (cf. expandCodeSeparatorVariants), pour que la
 * saisie soit insensible aux tirets et aux espaces dans les deux sens. Ces
 * variantes portent sur le terme ENTIER : le code recollé n'ayant plus d'espace,
 * il ne s'aligne sur aucun mot du terme brut, donc il rejoint la condition en
 * tant que membre OR de premier niveau à côté du groupe AND tokenisé.
 *
 * Ex : buildSearchOrConditions(['first_name','email'], 'a,b')
 *      -> 'first_name.ilike.%ab%,email.ilike.%ab%'
 *      buildSearchOrConditions(['first_name','last_name'], 'jean dupont')
 *      -> 'and(or(first_name.ilike.%jean%,last_name.ilike.%jean%),or(first_name.ilike.%dupont%,last_name.ilike.%dupont%))'
 *      buildSearchOrConditions(['bpi_code'], 'SDBPI 2027 0040')
 *      -> 'and(or(bpi_code.ilike.%SDBPI%),or(bpi_code.ilike.%2027%),or(bpi_code.ilike.%0040%)),bpi_code.ilike.%SDBPI20270040%,bpi_code.ilike.%SDBPI-2027-0040%'
 */
export function buildSearchOrConditions(fields: readonly string[], rawTerm: string): string {
  if (fields.length === 0) return '';
  // L'expansion BED→BPI doit voir le terme complet AVANT la tokenisation pour
  // conserver les séparateurs avec espaces (`BED 0042`, `BED 2027 0042`). Les
  // mots de chaque terme étendu sont ensuite regroupés par position : BED et
  // BPI rejoignent ainsi le même groupe OR.
  const allWordVariants: string[][] = [];
  // Variantes normalisées du terme ENTIER (code recollé / re-tireté). Elles ne
  // peuvent pas passer par la tokenisation positionnelle — le recollé compte un
  // mot là où le terme brut en compte trois — d'où ce second seau.
  const wholeTermVariants: string[] = [];
  for (const expandedTerm of expandBpiBedTerms(rawTerm)) {
    for (const variant of expandCodeSeparatorVariants(expandedTerm)) {
      // Le terme brut est déjà couvert par la tokenisation ci-dessous.
      if (variant === expandedTerm) continue;
      const safeVariant = escapeOrSearchTerm(variant);
      if (safeVariant !== '' && !wholeTermVariants.includes(safeVariant)) {
        wholeTermVariants.push(safeVariant);
      }
    }

    // Filtre sur la forme ÉCHAPPÉE (un mot réduit à rien ne prend pas de rang)
    // mais on garde le mot BRUT : `escapeOrSearchTerm` échappe `_` en `\_`, ce
    // qui brouillerait la détection de séparateurs d'un code.
    const rawWords = expandedTerm.split(/\s+/).filter((word) => escapeOrSearchTerm(word) !== '');

    rawWords.forEach((word, index) => {
      const variants = allWordVariants[index] ?? [];
      for (const variant of expandCodeSeparatorVariants(word)) {
        const safeVariant = escapeOrSearchTerm(variant);
        if (safeVariant !== '' && !variants.includes(safeVariant)) variants.push(safeVariant);
      }
      allWordVariants[index] = variants;
    });
  }

  // Borne le nombre de mots : au-delà, l'URL PostgREST générée (mots × champs
  // conditions) deviendrait démesurée ; ignorer les mots excédentaires ne fait
  // qu'élargir les résultats (jamais en perdre).
  const wordVariants = allWordVariants.slice(0, 10);
  if (wordVariants.length === 0) {
    // Terme entièrement composé de blancs et/ou de caractères de syntaxe .or()
    // (`, ( )`) : il ne reste aucun contenu cherchable après nettoyage. On émet
    // une condition TOUJOURS FAUSSE plutôt qu'un `ilike.%%` qui matcherait
    // toutes les lignes (revue 2026-06-01).
    return alwaysFalseCondition(fields[0]);
  }
  const orGroup = (variants: string[]) =>
    fields.flatMap((field) => variants.map((variant) => `${field}.ilike.%${variant}%`)).join(',');
  if (wordVariants.length === 1) {
    // Terme d'un seul mot : les variantes du terme entier SONT celles de ce mot
    // (le dédoublonnage les absorbe) → la forme plate historique est conservée.
    const merged = [...wordVariants[0]];
    for (const variant of wholeTermVariants) {
      if (!merged.includes(variant)) merged.push(variant);
    }
    return orGroup(merged);
  }
  const tokenized = `and(${wordVariants.map((variants) => `or(${orGroup(variants)})`).join(',')})`;
  if (wholeTermVariants.length === 0) return tokenized;
  // `query.or(...)` enveloppe déjà la chaîne dans `or=(…)` : une virgule de
  // premier niveau ajoute donc un membre OR à côté du groupe AND tokenisé →
  // `(mot1 ET mot2 ET …) OU code recollé OU code re-tireté`.
  return `${tokenized},${orGroup(wholeTermVariants)}`;
}

/**
 * Issue #263 : un n° d'équipage tapé seul (« 0001 », « EQ 0001 ») ne cherche que
 * dans crew_number. Sinon les codes BPI `9274LBPI0001xx` matchent aussi « 0001 »
 * et l'équipage 0001 arrivait 92e sur 92 (numéros 0000-0399 touchés). Au-delà
 * de 4 chiffres, le terme peut être un fragment de code BPI : recherche complète.
 */
export function buildCrewSearchConditions(fields: readonly string[], rawTerm: string): string {
  const crewNumber = /^\s*(?:eq[\s\-_]*)?(\d{1,4})\s*$/i.exec(rawTerm)?.[1];
  if (crewNumber && fields.includes('crew_number')) {
    return buildSearchOrConditions(['crew_number'], crewNumber);
  }
  return buildSearchOrConditions(fields, rawTerm);
}
